import moment from "moment";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  getActualWeekExercises,
  getExerciseWeek,
} from "../../../../utils/cycle-helpers";
import { SlotExerciseList } from "../../common-components/ProgramSlotCard";

const prettyShort = (yyyymmdd) =>
  yyyymmdd ? moment(String(yyyymmdd), "YYYYMMDD").format("MMM D") : "";

const prettyDay = (yyyymmdd) =>
  yyyymmdd ? moment(String(yyyymmdd), "YYYYMMDD").format("ddd MMM D") : "";

/**
 * The six week columns of the Flexible Program preview.
 *
 * Every slot is rendered by the manager's own SlotExerciseList - the only
 * difference is what it's fed. An unclaimed slot gets the week's template
 * (prescribed targets); a claimed slot gets the client's real dated copy,
 * whose `sets[].outcomes` SlotExerciseList already renders as logged results
 * with completed/missed dots.
 *
 * Claims come from flexible-program-overview's ledger. A reclaim (re-adding
 * an already-claimed slot onto a second date) writes no ledger entry, so
 * that second copy doesn't show here - the Program Manager has the same
 * blind spot.
 */
const FlexibleProgramPreviewStrip = ({ locals, api }) => {
  const { t } = useTranslation("coaching-portal");
  const { withWeight, fromMetric, activeUserToRequest } = locals;
  const cycle = locals?.editCycle;
  const athlete = locals?.query?.athlete;
  const cards = locals?.previewWindow?.cards || [];
  const occurrences = locals?.flexibleProgramOverview?.occurrences || [];

  const intensityMeasure =
    (activeUserToRequest?.intensityMeasure ?? "rpe") === "rpe" ? "RPE" : "RiR";
  const isRiR = intensityMeasure === "RiR";
  const units = activeUserToRequest?.units;

  const [templates, setTemplates] = useState({});
  const [actual, setActual] = useState(null);
  const [loading, setLoading] = useState(true);

  const occurrenceFor = (card) =>
    card.resolved?.status === "active"
      ? occurrences.find(
          (occurrence) =>
            Number(occurrence.occurrenceStartDate) ===
            Number(card.resolved.occurrenceStartDate)
        ) || null
      : null;

  const cardsKey = cards.map((card) => card.cardStart).join(",");

  // biome-ignore lint/correctness/useExhaustiveDependencies: refetch only when the visible window moves; templates are a cache, not an input
  useEffect(() => {
    let cancelled = false;

    const activeCards = cards.filter(
      (card) => card.resolved?.status === "active"
    );
    const missingOrders = [
      ...new Set(activeCards.map((card) => Number(card.resolved.programOrder))),
    ].filter((order) => !templates[order]);

    // Only fetch real exercises across the cards that actually have claims.
    const claimedCards = activeCards.filter(
      (card) => occurrenceFor(card)?.claims?.length
    );

    const load = async () => {
      setLoading(true);
      const [fetchedTemplates, fetchedActual] = await Promise.all([
        Promise.all(
          missingOrders.map(async (order) => {
            const response = await getExerciseWeek(api, cycle._id, order);
            return [
              order,
              {
                // An authored week is always a single week, so "1" is the
                // only key - same unwrap as the manager's WeekRow.
                exercises: response?.exercises?.["1"] || {},
                sections: response?.sections || [],
              },
            ];
          })
        ),
        claimedCards.length
          ? getActualWeekExercises(api, {
              from: claimedCards[0].cardStart,
              to: claimedCards[claimedCards.length - 1].cardEnd,
              proxyAthlete: athlete,
            })
          : Promise.resolve({ exercises: [], sections: [] }),
      ]);

      if (cancelled) return;
      setTemplates((previous) => ({
        ...previous,
        ...Object.fromEntries(fetchedTemplates),
      }));
      setActual(fetchedActual);
      setLoading(false);
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [cardsKey]);

  if (!cards.length) {
    return (
      <section className="no-data-state">
        <p className="s1">{t("flexible.manager.noWeeks")}</p>
      </section>
    );
  }

  const today = Number(moment().format("YYYYMMDD"));

  return (
    <div className="programs flexible-preview-strip">
      {cards.map((card) => (
        <WeekPreviewCard
          key={`preview-week-${card.cardStart}`}
          card={card}
          occurrence={occurrenceFor(card)}
          cycleId={String(cycle._id)}
          template={
            card.resolved?.status === "active"
              ? templates[Number(card.resolved.programOrder)]
              : null
          }
          actual={actual}
          loading={loading}
          isCurrent={today >= card.cardStart && today <= card.cardEnd}
          withWeight={withWeight}
          fromMetric={fromMetric}
          intensityMeasure={intensityMeasure}
          isRiR={isRiR}
          units={units}
          t={t}
        />
      ))}
    </div>
  );
};

/**
 * The client's real exercises for one claimed slot. Date alone isn't enough -
 * two slots can be claimed onto the same day - so the slot's own session
 * order is part of the match.
 */
const claimedExercises = (actual, claim, cycleId, programOrder, slotOrder) =>
  (actual?.exercises || []).filter(
    (exercise) =>
      Number(exercise.utcDate) === Number(claim.date) &&
      String(exercise.cycle?.id) === cycleId &&
      Number(exercise.cycle?.program) === programOrder &&
      Number(exercise.cycle?.programSession) === slotOrder
  );

/**
 * Claimed sections carry no cycle ref (just user + date), and the claim slots
 * each one's order between its own exercises' orders - so on a shared day,
 * the order range is what tells one slot's sections from the other's.
 */
const claimedSections = (actual, claim, exercises) => {
  if (!exercises.length) return [];
  const orders = exercises.map((exercise) => Number(exercise.order));
  const low = Math.min(...orders) - 1;
  const high = Math.max(...orders);
  return (actual?.sections || []).filter(
    (section) =>
      Number(section.utcDate) === Number(claim.date) &&
      Number(section.order) > low &&
      Number(section.order) < high
  );
};

const WeekPreviewCard = ({
  card,
  occurrence,
  cycleId,
  template,
  actual,
  loading,
  isCurrent,
  withWeight,
  fromMetric,
  intensityMeasure,
  isRiR,
  units,
  t,
}) => {
  const { resolved } = card;
  const status = resolved?.status;
  const program = resolved?.program;
  const programOrder = Number(resolved?.programOrder);

  const sessions = [...(program?.sessions || [])].sort(
    (a, b) => a.order - b.order
  );

  const claimByOrder = {};
  for (const claim of occurrence?.claims || [])
    claimByOrder[Number(claim.order)] = claim;

  return (
    <article
      className={`flexible-preview-week${isCurrent ? " is-current" : ""}`}
    >
      <div className="flexible-preview-week-header">
        <p className="s1">
          {prettyShort(card.cardStart)} &ndash; {prettyShort(card.cardEnd)}
        </p>
        {status === "active" && (
          <p className="p4 opacity-70">
            {program?.name || ""}
            {resolved.isRepeat && ` · ${t("flexible.preview.repeat")}`}
          </p>
        )}
        {isCurrent && (
          <span className="active-week-indicator w-fit">
            {t("flexible.manager.activeWeek")}
          </span>
        )}
        {status === "active" && (
          <p className="p4 opacity-70">
            {t("flexible.preview.scheduledCount", {
              claimed: occurrence?.claimedCount || 0,
              total: occurrence?.total ?? sessions.length,
            })}
          </p>
        )}
      </div>

      {status === "ended" && (
        <p className="p4 opacity-70 flexible-preview-empty">
          {t("flexible.preview.ended", {
            start: prettyShort(resolved.occurrenceStartDate),
            end: prettyShort(resolved.occurrenceEndDate),
          })}
        </p>
      )}

      {status === "beforeStart" && (
        <p className="p4 opacity-70 flexible-preview-empty">
          {t("flexible.manager.notStarted")}
        </p>
      )}

      {status === "active" &&
        (loading ? (
          <WeekCardSkeleton count={sessions.length || 3} />
        ) : (
          sessions.map((session) => {
            const slotOrder = Number(session.order);
            const claim = claimByOrder[slotOrder];

            const logged = claim
              ? claimedExercises(
                  actual,
                  claim,
                  cycleId,
                  programOrder,
                  slotOrder
                )
              : [];
            // Falls back to the template if the client has since deleted the
            // scheduled copy from their calendar.
            const useLogged = logged.length > 0;
            const exercises = useLogged
              ? logged
              : template?.exercises?.[slotOrder] || [];
            const sections = useLogged
              ? claimedSections(actual, claim, logged)
              : (template?.sections || []).filter(
                  (section) =>
                    section.cycle &&
                    section.cycle.programWeek === 1 &&
                    section.cycle.programSession === slotOrder
                );

            return (
              <div
                key={`${card.cardStart}-slot-${slotOrder}`}
                className="flexible-preview-slot"
              >
                <div className="flex flex-col gap-0.5">
                  <p className="s1">
                    {t("flexible.manager.slotLabel", { index: slotOrder })}
                    {session.workoutName && (
                      <span className="p4 opacity-70 ml-2">
                        {session.workoutName}
                      </span>
                    )}
                  </p>
                  <p
                    className={`p4 ${claim?.trained ? "flexible-preview-trained" : "opacity-70"}`}
                  >
                    {claim
                      ? t("flexible.manager.scheduledOn", {
                          date: prettyDay(claim.date),
                        })
                      : t("flexible.manager.notScheduled")}
                    {claim?.trained && ` ${t("flexible.manager.trained")}`}
                  </p>
                </div>

                {exercises.length ? (
                  <SlotExerciseList
                    session={exercises}
                    sessionSections={sections}
                    keyPrefix={`${card.cardStart}-${slotOrder}`}
                    withWeight={withWeight}
                    fromMetric={fromMetric}
                    intensityMeasure={intensityMeasure}
                    isRiR={isRiR}
                    units={units}
                  />
                ) : (
                  <p className="p4 opacity-50">
                    {t("flexible.preview.emptySlot")}
                  </p>
                )}
              </div>
            );
          })
        ))}
    </article>
  );
};

/** Stacked slot placeholders for one week column while its data loads. */
const WeekCardSkeleton = ({ count }) =>
  Array.from({ length: count }, (_, index) => (
    <div
      // biome-ignore lint/suspicious/noArrayIndexKey: static placeholders
      key={`skeleton-${index}`}
      className="flexible-preview-slot animate-pulse"
    >
      <div className="h-4 w-10 rounded-sm bg-(--white-10)" />
      <div className="h-16 rounded-lg bg-(--white-05)" />
    </div>
  ));

export default FlexibleProgramPreviewStrip;
